import { MouseEvent, PropsWithChildren, SyntheticEvent, useState } from "react";
import { Bar } from "react-chartjs-2";
import { useForm, useWatch } from "react-hook-form";
import { ArrowDropDown, InfoOutlined } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";
import {
  Badge,
  Box,
  Button,
  CardContent,
  Popover,
  Stack,
  Tab,
  Typography as T,
  Tooltip,
  styled,
  useTheme,
  paperClasses,
} from "@mui/material";
import FilterCheckbox from "catalog/components/FilterCheckbox";
import QuestionDetail from "catalog/components/QuestionDetail";
import { useConceptDetailsMulti } from "catalog/hooks/useConceptDetails";
import { QuestionGroupDetails } from "catalog/types/interfaces/conceptResponse";
import { DEFAULT_OPTS, mapResponseBreakdown } from "catalog/utils/chart";
import { ChartOptions } from "chart.js";
import { merge } from "chart.js/helpers";
import ContainedTabs from "~/components/ContainedTabs";
import FlatCard from "~/components/FlatCard";
import ListCard, { ExpandableListItem, ListItem } from "~/components/ListCard";

const numFormatter = new Intl.NumberFormat("en");
const compactFormatter = new Intl.NumberFormat("en", { notation: "compact" });

const STACKED_BAR_CHART_OPTS: ChartOptions<"bar"> = merge<ChartOptions<"bar">, ChartOptions<"bar">>(
  {
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        title: {
          display: true,
          text: "Survey Year",
        },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: "Response Count",
        },
        ticks: {
          callback: (value) => {
            if (!isNaN(Number(value))) {
              return compactFormatter.format(Number(value));
            }
            return value;
          },
        },
      },
    },
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          usePointStyle: true,
          pointStyle: "rect",
        },
      },
      tooltip: {
        axis: "x",
        intersect: false,
      },
    },
  },
  DEFAULT_OPTS
);

interface FilterOptions {
  survey_years: string[];
}

const InfoIcon = styled(InfoOutlined)(({ theme }) => ({
  opacity: 0.5,
  fontSize: 18,
  color: theme.palette.primary.dark,
}));

const CountLabel = (props: PropsWithChildren<{ count: number }>) => (
  <Stack flex={1} alignItems="center" justifyContent="center">
    <T component="span" variant="h5" fontWeight={500}>
      {numFormatter.format(props.count)}
    </T>
    <T variant="body2" sx={{ opacity: 0.85 }}>
      {props.children}
    </T>
  </Stack>
);

const TotalCountsCard = (props: { total: number; answeredAll: number; answeredOne: number }) => (
  <FlatCard>
    <CardContent sx={{ flexDirection: "row", gap: 1, "&:last-child": { pb: 2 } }}>
      <CountLabel count={props.answeredOne}>
        <Box component="span" display="flex" gap={0.5} alignItems="center" whiteSpace="nowrap">
          Total Participants Count
          <Tooltip title="The number of participants who have answered the selected survey question.">
            <InfoIcon />
          </Tooltip>
        </Box>
      </CountLabel>
    </CardContent>
  </FlatCard>
);

const QuestionFilter = (props: {
  options: FilterOptions;
  selected: FilterOptions;
  onSelected: (filters: FilterOptions) => void;
}) => {
  const { palette } = useTheme();
  const { control, handleSubmit, reset } = useForm<FilterOptions>({
    defaultValues: props.selected,
  });
  const filtersWatch = useWatch<FilterOptions>({ control, name: ["survey_years"] });
  const filtersSelected = !!filtersWatch.flatMap((val) => val).length;
  const filtersLength = Object.values(props.selected).flatMap((val) => val).length;
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onSubmit = (data: FilterOptions) => {
    props.onSelected(data);
    handleClose();
  };

  const handleClear = () => {
    reset({ survey_years: [] });
    props.onSelected({ survey_years: [] });
    handleClose();
  };

  return (
    <>
      <Badge
        badgeContent={filtersLength}
        color="primary"
        sx={{
          "& > span": {
            fontSize: 10,
            top: 2,
            height: 16,
            minWidth: 16,
            fontWeight: 500,
            outline: `3px solid ${palette.common.white}`,
            userSelect: "none",
          },
        }}
      >
        <Button
          size="small"
          variant="contained"
          color="secondary"
          endIcon={<ArrowDropDown />}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          }}
          sx={{
            py: 1,
            bgcolor: "grey.200",
            "&:hover": {
              "&:hover": {
                bgcolor: "grey.300",
              },
            },
          }}
        >
          Survey Year
        </Button>
      </Badge>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          handleClose();
          reset(props.selected);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionProps={{ timeout: 0 }}
        PaperProps={{
          elevation: 2,
        }}
        sx={{
          mt: 1,
        }}
      >
        <Stack
          component="form"
          sx={{ userSelect: "none", gap: 1, px: 2, py: 1.5 }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Box display="grid" columnGap={1} gridTemplateColumns="repeat(2, 1fr)">
            {props.options.survey_years.map((val) => (
              <FilterCheckbox<FilterOptions>
                key={val}
                name="survey_years"
                facetKey={val}
                label={
                  <T component="span" fontSize={14} color="inherit" lineHeight={1}>
                    {val}
                  </T>
                }
                control={control}
              />
            ))}
          </Box>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
            <Button
              variant="outlined"
              color="inherit"
              disabled={!filtersSelected}
              onClick={handleClear}
            >
              Clear
            </Button>
            <Button type="submit" variant="contained">
              Apply
            </Button>
          </Box>
        </Stack>
      </Popover>
    </>
  );
};

const QuestionListCard = (props: { questions: QuestionGroupDetails["questions"] }) => {
  const [filters, setFilters] = useState<FilterOptions>({ survey_years: [] });
  const uniqueYears = new Set(
    props.questions
      .filter(({ survey_year }) => survey_year)
      .map(({ survey_year }) => String(survey_year))
  );
  const sortedYears = Array.from(uniqueYears).sort();

  const sortedQuestions = [...props.questions]
    .filter((question) =>
      filters.survey_years.length
        ? filters.survey_years.includes(String(question.survey_year))
        : question.survey_year
    )
    .sort((a, b) => {
      const questionNumberA = Number(a.concept_name.split(".")[0]);
      const questionNumberB = Number(b.concept_name.split(".")[0]);
      const surveyNameComparison = a.concept_name?.localeCompare(b.concept_name);

      // Sort by year (oldest -> newest)
      if (a.survey_year && b.survey_year) {
        if (a.survey_year < b.survey_year) {
          return -1;
        }
        if (a.survey_year > b.survey_year) {
          return 1;
        }
      }

      // Sort by survey name
      if (surveyNameComparison !== 0) {
        return surveyNameComparison;
      }

      // Sort by question number
      return questionNumberA - questionNumberB;
    });

  const { data } = useConceptDetailsMulti(sortedQuestions.map(({ concept_id }) => concept_id));
  const questionConceptDetails =
    data?.responses?.map((response) => response?.data?.question).filter(Boolean) ?? [];

  if (!sortedQuestions.length) {
    return null;
  }

  return (
    <ListCard
      borderTop
      header={
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            pt={2}
            px={2}
            gap={1}
          >
            <Box display="flex" alignItems="center" gap={0.5}>
              <T variant="body1" fontWeight={600}>
                Questions
              </T>
              <Tooltip
                arrow
                title="Verbiage of questions and question parts as presented in each survey along with their respondent count."
              >
                <InfoIcon />
              </Tooltip>
            </Box>
            {sortedYears.length > 1 && (
              <QuestionFilter
                options={{ survey_years: sortedYears }}
                selected={filters}
                onSelected={setFilters}
              />
            )}

            <T variant="body2" sx={{ opacity: 0.5, fontWeight: 500, textAlign: "end", pr: 0.5 }}>
              Total Responses
            </T>
          </Box>
        </Box>
      }
    >
      {sortedQuestions?.map((question) => {
        const details = questionConceptDetails.find(
          (detail) => detail?.concept_id === question.concept_id
        );
        const metadata = [question.survey_year, question.column_mapping?.table_name]
          .filter(Boolean)
          .join(" · ");

        const summary = (
          <>
            <Stack flex={1} gap={1}>
              <T variant="body1">{question.concept_name}</T>
              <T variant="body2" sx={{ opacity: 0.6 }}>
                {metadata}
              </T>
            </Stack>
            <T
              variant="body1"
              sx={{
                opacity: 0.6,
                display: "flex",
                alignItems: "center",
                justifyContent: "end",
                minWidth: 75,
                gap: 0.5,
                ml: 1,
              }}
            >
              {numFormatter.format(question.response_count)}
            </T>
          </>
        );

        if (!details) {
          return <ListItem key={question.concept_id}>{summary}</ListItem>;
        }

        return (
          <ExpandableListItem
            key={question.concept_id}
            expansionContent={
              <Box sx={{ [`.${paperClasses.root}`]: { border: "none" } }}>
                <QuestionDetail data={details} />
              </Box>
            }
          >
            {summary}
          </ExpandableListItem>
        );
      })}
    </ListCard>
  );
};

const QuestionGroupDetail = (props: { data: QuestionGroupDetails }) => {
  const [chartTab, setChartTab] = useState("gender");

  return (
    <>
      <TotalCountsCard
        total={props.data.response_count}
        answeredAll={props.data.participant_answered_all_count}
        answeredOne={props.data.participant_answered_at_least_one_count}
      />
      <QuestionListCard questions={props.data.questions} />
      {props.data.response_count > 0 && (
        <FlatCard>
          <CardContent>
            <TabContext value={chartTab}>
              <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <T variant="body1" fontWeight={600}>
                    Response Breakdown
                  </T>
                  <Tooltip title="Distribution of participants who answered questions in each survey year. Broken down by race and gender.">
                    <InfoIcon />
                  </Tooltip>
                </Box>
                <ContainedTabs
                  value={chartTab}
                  onChange={(_: SyntheticEvent, value: any) => setChartTab(value)}
                >
                  <Tab label="Gender" value="gender" />
                  <Tab label="Race" value="race" />
                </ContainedTabs>
              </Box>
              <TabPanel value="race" className="chart-container">
                <Bar
                  data={mapResponseBreakdown(props.data.participant_breakdown.race_counts)}
                  options={STACKED_BAR_CHART_OPTS}
                />
              </TabPanel>
              <TabPanel value="gender" className="chart-container">
                <Bar
                  data={mapResponseBreakdown(props.data.participant_breakdown.gender_counts)}
                  options={STACKED_BAR_CHART_OPTS}
                />
              </TabPanel>
            </TabContext>
          </CardContent>
        </FlatCard>
      )}
    </>
  );
};

export default QuestionGroupDetail;
